/**
 * Authentication initialization logic for router beforeLoad
 */

import {
	clearAllAuthData,
	createAuthFlow,
	getOAuthTokensFromStorage,
	hasAuthToken,
	hasOAuthTokensInStorage,
	saveAuthenticationTokens,
} from "@medme/auth"
import * as Sentry from "@sentry/react"
import { getAppConfig } from "@/app/config/app-config"
import { customFetch as customOAuthFetch } from "@/shared/api/custom-oauth-client"
import {
	exchangeAuthCode,
	getExchangeAuthCodeMutationOptions,
} from "@/shared/api/generated/oauth/oauth-api"
import { ExchangeAuthCodeResponse } from "@/shared/api/generated/oauth/oauth-api.zod"
import { getUserGetUserBusinessesQueryOptions } from "@/shared/api/generated/user/user"
import { validateSuccessResponse } from "@/shared/lib/zod-validation-helpers"
import type { AuthState, InitAuthParams } from "./types"

const { establishAuthSession, refreshAuthTokens, logout } =
	createAuthFlow(customOAuthFetch)

/**
 * Initialize authentication state
 * Handles OAuth code exchange, token refresh, and JWT authentication
 *
 * Initialized at root level in beforeLoad. On page preload will
 * check user businesses, otherwise refreshes tokens via oauth & authenticates to stats service
 */
export async function initAuthAsync({
	oauthCode,
	queryClient,
}: InitAuthParams): Promise<AuthState> {
	// Check if JWT token already exists
	if (await hasAuthToken()) {
		return createAuthState(true)
	}

	// Handle OAuth code from URL
	if (oauthCode) {
		try {
			const exchangeResponse = await queryClient.fetchQuery({
				queryKey: getExchangeAuthCodeMutationOptions().mutationKey ?? [],
				queryFn: () =>
					exchangeAuthCode({
						client_id: getAppConfig().clientId,
						code: oauthCode,
						redirect_uri: window.location.origin + window.location.pathname,
					}),
			})

			const exchangeTokens = validateSuccessResponse(
				ExchangeAuthCodeResponse,
				exchangeResponse,
			)

			if (!exchangeTokens) {
				throw new Error("Exchange tokens not found in exchange response")
			}

			// Sets JWT token in cookie через OAuth-сервер (Domain=.${DOMAIN})
			await establishAuthSession(exchangeTokens.user, exchangeTokens.token)

			// Save all tokens. Token already saved to cookie by previous call.
			saveAuthenticationTokens(exchangeTokens)
			localStorage.setItem("user_id", exchangeTokens.user)

			Sentry.addBreadcrumb({
				category: "auth",
				message: "OAuth authentication successful",
				level: "info",
			})

			return createAuthState(true)
		} catch (err) {
			const error =
				err instanceof Error ? err : new Error("OAuth authentication failed")
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "oauth_exchange" },
				extra: { masked: true },
			})
			await clearAllAuthData()
			return createAuthState(false, error)
		}
	}

	// Check if OAuth tokens exist in storage
	if (hasOAuthTokensInStorage()) {
		// check whether user authorized by checking if he has access to any business
		try {
			const userBusinesses = await queryClient.ensureQueryData(
				getUserGetUserBusinessesQueryOptions({
					query: {
						retry: 1,
					},
				}),
			)
			if (userBusinesses.status !== 401) {
				return createAuthState(true)
			}
		} catch (error) {
			console.error("Error checking user businesses:", error)
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "check_user_businesses" },
			})
		}

		const refreshResult = await refreshAuthTokens()

		if (refreshResult) {
			saveAuthenticationTokens({
				token: refreshResult.access_token,
				refreshToken: refreshResult.refresh_token,
			})

			Sentry.addBreadcrumb({
				category: "auth",
				message: "JWT authentication successful via token refresh",
				level: "info",
			})

			return createAuthState(true)
		}

		// If refresh failed, try regular authentication
		try {
			const storedTokens = getOAuthTokensFromStorage()
			const userId = localStorage.getItem("user_id")
			if (!storedTokens || !userId) {
				return createAuthState(false)
			}

			// Sets JWT token in cookie через OAuth-сервер (Domain=.${DOMAIN})
			await establishAuthSession(userId, storedTokens.token)

			Sentry.addBreadcrumb({
				category: "auth",
				message: "JWT authentication successful",
				level: "info",
			})

			return createAuthState(true)
		} catch (err) {
			const error =
				err instanceof Error ? err : new Error("JWT authentication failed")
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "jwt_auth" },
				extra: { masked: true },
			})
			await clearAllAuthData()
			return createAuthState(false, error)
		}
	}

	return createAuthState(false)
}

/**
 * Create authentication state object
 */
function createAuthState(
	isAuthenticated: boolean,
	error: Error | null = null,
): AuthState {
	return {
		isAuthenticated,
		isAuthenticating: false,
		error,
		logout: () => {
			logout(getAppConfig().clientId).finally(() => {
				window.location.reload()
			})
		},
	}
}
